import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';

// Comprehensive MET table — 120+ activities grouped by category (Ainsworth compendium subset)
const MET_CAT = {
  'Walking / Hiking': {
    'Walking, 2 mph (30 min/mi)': 2.5, 'Walking, 2.5 mph (24 min/mi)': 3.0,
    'Walking, 3 mph (20 min/mi)': 3.3, 'Walking, 3.5 mph (17 min/mi)': 3.8,
    'Walking, 4 mph (15 min/mi)': 5.0, 'Walking, 4.5 mph (13 min/mi)': 7.0,
    'Walking upstairs': 8.0, 'Hiking, cross-country': 6.0, 'Hiking with 10-20 lb pack': 7.5,
    'Backpacking, uphill': 9.0, 'Stair climbing (slow)': 4.0, 'Stair climbing (fast)': 8.8,
  },
  'Running / Jogging': {
    'Jogging, 5 mph (12 min/mi)': 8.3, 'Running, 6 mph (10 min/mi)': 9.8,
    'Running, 6.7 mph (9 min/mi)': 10.5, 'Running, 7.5 mph (8 min/mi)': 11.5,
    'Running, 8.6 mph (7 min/mi)': 12.3, 'Running, 10 mph (6 min/mi)': 14.5,
    'Running, 11 mph (5.5 min/mi)': 16.0, 'Running in place': 8.0,
    'Trail running': 10.0, 'Cross-country skiing race': 15.0,
  },
  'Cycling': {
    'Cycling <10 mph (leisure)': 4.0, 'Cycling 10-12 mph (light)': 6.8,
    'Cycling 12-14 mph (moderate)': 8.0, 'Cycling 14-16 mph (vigorous)': 10.0,
    'Cycling 16-19 mph (racing)': 12.0, 'Cycling >20 mph (racing)': 16.0,
    'Mountain biking': 8.5, 'Bike commuting (to work)': 6.8, 'Stationary bike, light': 5.5,
    'Stationary bike, moderate': 6.8, 'Stationary bike, vigorous': 10.5,
  },
  'Swimming / Water': {
    'Swimming laps, slow freestyle': 5.8, 'Swimming laps, moderate freestyle': 8.3,
    'Swimming laps, fast freestyle': 9.8, 'Swimming, backstroke': 7.0,
    'Swimming, breaststroke': 10.0, 'Swimming, butterfly': 13.8,
    'Water aerobics': 5.5, 'Water polo': 10.0, 'Snorkeling': 5.0,
    'Scuba diving': 7.0, 'Surfing': 3.0, 'Kayaking': 5.0, 'Rowing (canoeing)': 5.8,
  },
  'Gym / Weight Training': {
    'Weight lifting, light': 3.5, 'Weight lifting, moderate': 5.0,
    'Weight lifting, vigorous': 6.0, 'Circuit training': 8.0,
    'Powerlifting': 6.0, 'CrossFit': 12.0, 'Bodyweight training': 3.8,
    'Rowing machine, light': 4.8, 'Rowing machine, moderate': 7.0,
    'Rowing machine, vigorous': 8.5, 'Elliptical trainer': 5.0,
    'Stair-master, light': 5.0, 'Stair-master, moderate': 8.8,
  },
  'Aerobics / Dance': {
    'Aerobics, low impact': 5.0, 'Aerobics, high impact': 7.3, 'Step aerobics': 8.5,
    'Zumba': 7.5, 'Dancing, slow (ballroom)': 3.0, 'Dancing, aerobic': 6.5,
    'Dancing, ballet': 4.8, 'Dancing, salsa': 5.5, 'Dancing, hip-hop': 6.0,
    'Yoga (Hatha)': 2.5, 'Yoga (Vinyasa/power)': 4.0, 'Pilates': 3.0,
    'Jumping rope, slow': 8.8, 'Jumping rope, moderate': 11.8, 'Jumping rope, fast': 12.3,
    'Trampoline, moderate': 4.5,
  },
  'Sports': {
    'Basketball, casual': 6.5, 'Basketball, game': 8.0, 'Soccer, casual': 7.0,
    'Soccer, competitive': 10.0, 'Tennis, doubles': 6.0, 'Tennis, singles': 8.0,
    'Golf, walking': 4.8, 'Golf, cart': 3.5, 'Baseball / softball': 5.0,
    'Football (American)': 8.0, 'Football (touch)': 8.0, 'Volleyball, casual': 3.0,
    'Volleyball, competitive': 6.0, 'Badminton': 5.5, 'Table tennis': 4.0,
    'Racquetball': 7.0, 'Squash': 12.0, 'Boxing (sparring)': 9.0,
    'Boxing (bag)': 5.5, 'Martial arts (karate, judo)': 10.3, 'Wrestling': 6.0,
    'Skateboarding': 5.0, 'Rollerblading': 7.5, 'Ice skating': 5.5, 'Ice hockey': 8.0,
  },
  'Winter / Outdoor': {
    'Skiing, downhill (light)': 4.3, 'Skiing, downhill (moderate)': 5.3,
    'Skiing, cross-country (moderate)': 7.5, 'Snowboarding': 5.3,
    'Snow shoveling, moderate': 5.3, 'Snow shoveling, heavy': 7.5,
    'Ice skating, moderate': 5.5, 'Sledding': 7.0, 'Snowmobiling': 3.5,
  },
  'Household / Daily': {
    'Cooking': 2.5, 'Dishwashing (hand)': 2.5, 'Vacuuming': 3.3,
    'Sweeping / mopping': 3.3, 'Washing car': 3.5, 'Ironing': 1.8,
    'Grocery shopping (walking)': 3.5, 'Playing with children (moderate)': 4.0,
    'Carrying children': 3.5, 'Moving furniture': 5.8, 'Gardening (light)': 3.5,
    'Gardening (heavy)': 6.0, 'Mowing lawn (power)': 4.5,
    'Mowing lawn (manual push)': 6.0, 'Raking leaves': 4.3,
    'Sitting (reading, watching TV)': 1.3, 'Standing (light work)': 2.0,
  },
};

// Flatten for lookup
const MET_FLAT = Object.assign({}, ...Object.values(MET_CAT));

export default function CaloriesBurnedCalc() {
  const [tab, setTab] = useState(0);
  const [activity, setActivity] = useState('Running, 6 mph (10 min/mi)');
  const [category, setCategory] = useState('Running / Jogging');
  const [lb, setLb] = useState(160);
  const [kg, setKg] = useState(73);
  const [minutes, setMinutes] = useState(30);
  const clear = () => { setActivity('Running, 6 mph (10 min/mi)'); setCategory('Running / Jogging'); setLb(160); setKg(73); setMinutes(30); };

  const weightKg = tab === 0 ? (+lb || 0) * 0.453592 : +kg || 0;

  const c = useMemo(() => {
    const met = MET_FLAT[activity] || 1;
    const calMin = met * 3.5 * weightKg / 200;
    const calTotal = calMin * (+minutes || 0);
    return { met, calMin, calTotal, calHour: calMin * 60 };
  }, [activity, weightKg, minutes]);

  // Compare table: same duration + weight across popular activities
  const compare = useMemo(() => {
    const popular = [
      'Walking, 3 mph (20 min/mi)', 'Walking, 4 mph (15 min/mi)',
      'Jogging, 5 mph (12 min/mi)', 'Running, 6 mph (10 min/mi)', 'Running, 7.5 mph (8 min/mi)',
      'Cycling 10-12 mph (light)', 'Cycling 12-14 mph (moderate)', 'Cycling 14-16 mph (vigorous)',
      'Swimming laps, moderate freestyle', 'Swimming, breaststroke',
      'Weight lifting, moderate', 'Weight lifting, vigorous', 'CrossFit',
      'Jumping rope, moderate', 'Aerobics, high impact', 'Rowing machine, moderate',
      'Basketball, game', 'Soccer, competitive', 'Tennis, singles',
      'Yoga (Hatha)', 'Pilates', 'Boxing (sparring)',
    ];
    return popular.map(name => {
      const met = MET_FLAT[name];
      const cal = met * 3.5 * weightKg / 200 * (+minutes || 0);
      return { name, met, cal };
    }).sort((a, b) => b.cal - a.cal);
  }, [weightKg, minutes]);

  return (
    <CalcShell look="cnet" title="Calories Burned Calculator" subtitle="Estimate calories expended in 120+ activities and compare popular ones side-by-side.">
      <CalcNetPanel
        slug="cb"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        right={
          <div>
            <div className="text-lg text-slate-800 mb-1">Total Calories Burned</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="cb-total">{Math.round(c.calTotal).toLocaleString()} kcal</div>
            <ResultTable
              testid="cb-detail-table"
              rows={[
                ['MET value', c.met.toFixed(1)],
                ['Calories per minute', `${c.calMin.toFixed(2)} kcal`],
                ['Calories per hour', `${Math.round(c.calHour).toLocaleString()} kcal`],
                ['Weight used (kg)', weightKg.toFixed(1)],
              ]}
            />
            <p className="mt-3 text-xs text-slate-600">Formula: Calories/min = MET × 3.5 × weight (kg) / 200</p>
          </div>
        }
      >
        <Row label="Category">
          <select value={category} onChange={e => { setCategory(e.target.value); const first = Object.keys(MET_CAT[e.target.value])[0]; setActivity(first); }} data-testid="cb-category"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            {Object.keys(MET_CAT).map(k => <option key={k}>{k}</option>)}
          </select>
        </Row>
        <Row label="Activity">
          <select value={activity} onChange={e => setActivity(e.target.value)} data-testid="cb-activity"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            {Object.keys(MET_CAT[category] || {}).map(k => <option key={k}>{k} (MET {MET_CAT[category][k]})</option>)}
          </select>
        </Row>
        <Row label="Weight">{tab === 0
          ? <UnitInput value={lb} onChange={setLb} unit="pounds" testid="cb-wlb" />
          : <UnitInput value={kg} onChange={setKg} unit="kg" testid="cb-wkg" />}</Row>
        <Row label="Duration"><UnitInput value={minutes} onChange={setMinutes} unit="minutes" testid="cb-mins" /></Row>
      </CalcNetPanel>

      <div className="mt-6">
        <h3 className="font-bold text-slate-900 mb-2">Compare Popular Activities</h3>
        <p className="text-xs text-slate-600 mb-2">All values below use your current weight ({weightKg.toFixed(1)} kg) and duration ({minutes} minutes). Sorted by calories burned.</p>
        <table className="w-full text-sm border border-slate-300" data-testid="cb-compare-table">
          <thead className="bg-[#1e40af] text-white">
            <tr><th className="px-3 py-2 text-left">Activity</th><th className="px-3 py-2 text-right">MET</th><th className="px-3 py-2 text-right">Calories ({minutes} min)</th><th className="px-3 py-2 text-right">Per hour</th></tr>
          </thead>
          <tbody>
            {compare.map((row, i) => (
              <tr key={row.name} className={`${i % 2 ? 'bg-slate-50' : ''} ${row.name === activity ? 'bg-emerald-100 font-bold' : ''}`}>
                <td className="px-3 py-1.5">{row.name}{row.name === activity ? ' (selected)' : ''}</td>
                <td className="px-3 py-1.5 text-right font-mono">{row.met.toFixed(1)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{Math.round(row.cal).toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right font-mono">{Math.round(row.met * 3.5 * weightKg / 200 * 60).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StepsPanel steps={[
        `Convert weight to kg: ${weightKg.toFixed(1)} kg`,
        `Cal/min = MET (${c.met}) × 3.5 × ${weightKg.toFixed(1)} / 200 ≈ ${c.calMin.toFixed(2)}`,
        `Total = ${c.calMin.toFixed(2)} × ${minutes} = <b>${Math.round(c.calTotal)} kcal</b>`,
      ]} />
    </CalcShell>
  );
}
